import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RedeemIcon from '@mui/icons-material/Redeem';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import {
  COLOR_GREEN,
  COLOR_PRIMARY,
  COLOR_RED,
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  DELETABLE_SUBSCRIPTION_STATUS,
  exchangeRates,
  YEARLY_UPGRADE_DISCOUNT_INVALID_FOR_STATUS,
} from 'src/utils/const';
import {
  COLOR_PRIMARY_2_100,
  COLOR_SUCCESS,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
  COLOR_TEXT_4,
  COLOR_TEXT_GREY,
} from 'src/utils/colors';

import { formatCurrency, formatDate } from 'src/utils/functions';
import { useSelector } from 'react-redux';
import { CircularProgressInText, NewSubscriptionDateText } from './CommonComponents';

const ActiveSubscriptionCard = ({
  setIsModalOpen,
  userSubscriptionDetails,
  setShowUpgradeYearlySubscriptionModal,
  finalDueAmount,
  adminDashboardView,
  hideUpgradeButton,
}: {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userSubscriptionDetails: any;
  setShowUpgradeYearlySubscriptionModal;
  finalDueAmount: number | null;
  adminDashboardView?: boolean;
  hideUpgradeButton?: boolean;
}) => {
  const [deleteButtonAnchorEl, setDeleteButtonAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userSubscriptionCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPlan = userSubscriptionDetails['subscription_plan'];
  const isFreeUpgradeFromPro = userSubscriptionDetails['is_free_upgrade_from_pro'];
  const nextPaymentDate = userSubscriptionDetails['next_payment_date'];
  const paymentFrequency = userSubscriptionDetails['payment_frequency'];
  const subscriptionStatus = userSubscriptionDetails['subscription_status'];
  const subscriptionEndDate = userSubscriptionDetails['subscription_end_date'];
  const subscriptionPrice = formatCurrency(
    String(
      CurrencyWithSubscriptionPrice[userSubscriptionCurrency][isFreeUpgradeFromPro ? 'pro' : subscriptionPlan][paymentFrequency]
    )
  );
  const currencySymbol = exchangeRates[userSubscriptionCurrency].symbol;

  const subHeaderPrefix = subscriptionStatus === 'active' ? 'Next billing' : subscriptionStatus;

  return (
    <>
      {/* Upgrade Plan Section */}
      {!adminDashboardView && !hideUpgradeButton && (
        <Typography variant="h6" sx={{ mb: '10px', color: COLOR_TEXT_GREY }}>
          Your Current Plan
        </Typography>
      )}

      <Card
        sx={{
          background: adminDashboardView ? 'linear-gradient(180deg, #E4F2F0 0%, #EEF6F5 100%)' : '#eee',
          boxShadow: adminDashboardView ? 'inset 0 0 0 1px rgba(3, 122, 144, 0.12)' : 'none',
          mb: '10px',
        }}
      >
        <CardHeader
          action={
            adminDashboardView ? (
              <></>
            ) : DELETABLE_SUBSCRIPTION_STATUS.includes(subscriptionStatus) ? (
              <Box sx={{ display: 'inline-box' }}>
                <IconButton onClick={event => setDeleteButtonAnchorEl(event.currentTarget)}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
            ) : paymentFrequency === 'monthly' && !YEARLY_UPGRADE_DISCOUNT_INVALID_FOR_STATUS.includes(subscriptionStatus) && !hideUpgradeButton? (
              <IconButton
                onClick={() => {
                  setShowUpgradeYearlySubscriptionModal(true);
                  setIsModalOpen(false);
                }}
              >
                <RedeemIcon sx={{ color: COLOR_PRIMARY }} />
              </IconButton>
            ) : (
              <></>
            )
          }
          title={
            <>
              {subscriptionPlan} Plan{' '}
              <Chip
                variant="outlined"
                label={subscriptionStatus}
                sx={{
                  color: subscriptionStatus === 'active' ? COLOR_GREEN : COLOR_RED,
                  borderColor: subscriptionStatus === 'active' ? COLOR_GREEN : COLOR_RED,
                  textTransform: 'capitalize',
                }}
              />
            </>
          }
          subheader={`${subHeaderPrefix} on ${formatDate(
            subscriptionStatus === 'active' ? nextPaymentDate : subscriptionEndDate
          )}`}
          sx={{
            paddingBottom: 0,
            '.MuiCardHeader-title': {
              fontSize: '20px',
              fontWeight: 500,
              color: COLOR_PRIMARY,
              textTransform: 'capitalize',
            },
            '.MuiCardHeader-subheader': {
              color: '#000',
              marginTop: '8px',
              textTransform: 'capitalize',
            },
          }}
        />
        <CardContent>
          <Typography
            variant="body1"
            sx={{ fontSize: '20px', fontWeight: 500, color: '#000', pt: 0 }}
          >
            {currencySymbol}
            {subscriptionPrice}{' '}
            <span style={{ fontSize: '14px', color: COLOR_TEXT_3 }}>
              /{paymentFrequency === 'yearly' ? 'year' : 'month'}
            </span>
          </Typography>
        </CardContent>
      </Card>

      <Popover
        open={Boolean(deleteButtonAnchorEl)}
        anchorEl={deleteButtonAnchorEl}
        onClose={() => setDeleteButtonAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disablePortal={false}
        sx={{ position: 'absolute' }}
      >
        <Box
          sx={{
            background: '#fff',
            padding: '20px',
            borderRadius: '4px',
          }}
        >
          <Button
            onClick={() => setIsModalOpen(true)}
            sx={{
              background: COLOR_RED,
              color: '#fff',
              '&:hover': {
                background: COLOR_RED + '88',
              },
            }}
          >
            UNSUBSCRIBE
          </Button>
        </Box>
      </Popover>

      {paymentFrequency === 'monthly' && !YEARLY_UPGRADE_DISCOUNT_INVALID_FOR_STATUS.includes(subscriptionStatus) && !hideUpgradeButton && (
        <>
          {/* Upgrade Plan Section */}
          <Typography variant="h6" sx={{ mt: '20px', color: COLOR_TEXT_GREY }}>
            Upgrade Your Plan
          </Typography>

          <Divider sx={{ mt: '10px', mb: '20px' }} />

          <Box
            sx={{
              position: 'relative',
              border: `2px solid ${COLOR_TEXT_4}`,
              borderRadius: '20px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              maxWidth: '500px',
              gap: '10px',
              mb: '40px',
              boxShadow: `8px 8px 0 2px ${COLOR_PRIMARY_2_100}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                fontWeight: 500,
                color: COLOR_TEXT_2,
              }}
            >
              🚀 Upgrade Yearly
            </Typography>
            <Typography variant="body1" sx={{ color: COLOR_TEXT_4 }}>
              We’d love to make Airbtics work for you!{' '}
              <strong
                style={{
                  padding: '2px 4px',
                  background: COLOR_SUCCESS + '30',
                  borderRadius: '2px',
                }}
              >
                Your next 6 months on us!
              </strong>{' '}
              Pay extra {currencySymbol}
              {finalDueAmount ? formatCurrency(String(finalDueAmount)) : CircularProgressInText} for
              additional 12 months.
              {NewSubscriptionDateText}
            </Typography>
            <Button
              onClick={() => setShowUpgradeYearlySubscriptionModal(true)}
              endIcon={<KeyboardDoubleArrowRightRoundedIcon sx={{ fontSize: '22px' }} />}
              variant="contained"
              sx={{ width: '140px', color: '#fff', mt: '10px', fontSize: '18px' }}
            >
              Claim It!
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default ActiveSubscriptionCard;
